import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import { UiHomePage } from '@experiences/home-page';
import {
    UiAlertBanner,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import LaunchIcon from '@mui/icons-material/Launch';
import { ApLink } from '@uipath/portal-shell-react';
import React, {
    useMemo,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import useSWR from 'swr';

import useCheckAuthTypes from '../../auth/hooks/CheckAuthType';
import useUserInfo from '../../auth/hooks/UserInfo';
import { hyperLink } from '../../common/constants/Constant';
import { useCheckSSOEnabled } from '../../common/hooks/useCheckSSOEnabled';
import useIsDedicated from '../../common/hooks/useIsDedicated';
import useIsOnPrem from '../../common/hooks/useIsOnPrem';
import useProfileStateName from '../../common/hooks/useProfileStateName';
import { getByoProductLlmConfigurations } from '../../services/llm-gateway/LlmGatewayService';
import {
    accountGlobalId,
    accountLogicalName,
    isAdminSelector,
    isUnlicensedSelector,
    profile,
} from '../../store/selectors';
import Carousel from '../carousel/CarouselComponent';
import { UiLicenseExpiredBanner } from '../common/UiLicenseExpiredBanner';
import UiLicenseInGracePeriodBanner from '../common/UiLicenseInGracePeriodBanner';
import UiLicenseReactivationNeededBanner from '../common/UiLicenseReactivationRequiredBanner';
import { UiPanel } from '../common/UiPanel/UiPanel';
import { UiTrialPerSkuLicenseInGracePeriodBanners } from '../common/UiTrialPerSkuLicenseInGracePeriodBanners';
import { UiUpcomingMigrationBanner } from '../common/UiUpcomingMigrationBanner/UiUpcomingMigrationBanner';
import { useTenantsContext } from '../tenants/TenantsContextProvider';
import { HomePageLicensesComponent } from './HomePageLicensesComponent';
import {
    HomePageModelValidationComponent,
    MODEL_VALIDATION_BANNER_DISMISSED,
} from './HomePageModelValidationComponent';
import { HomePageSidebarLinksComponent } from './HomePageSidebarLinksComponent';

const HomePageComponent = () => {

    const [ firstName, lastName ] = useProfileStateName();
    const { formatMessage: translate } = useIntl();
    const { token } = useUserInfo();
    const [ isBannerDismissed, setIsBannerDismissed ] = useState(sessionStorage.getItem(MODEL_VALIDATION_BANNER_DISMISSED) === 'true');

    const organizationName = useSelector(accountLogicalName);
    const organizationGuid = useSelector(accountGlobalId);
    const userProfile = useSelector(profile);
    const isUnlicensedMode = useSelector(isUnlicensedSelector);
    const isAdmin = useSelector(isAdminSelector);
    const isOnPrem = useIsOnPrem();
    const { checkTokenTypeIsAuth0: isAuth0 } = useCheckAuthTypes();

    const { selectedTenant } = useTenantsContext();
    const currentTenantId = selectedTenant?.id;

    const {
        data: llmConfigurations, isLoading: isLoadingLlmConfigs,
    } = useSWR(
        (isOnPrem && !isUnlicensedMode && isAdmin && currentTenantId && organizationGuid)
            ? [ 'product-llm-configurations', organizationGuid, currentTenantId ]
            : null,
        () => getByoProductLlmConfigurations(organizationGuid, currentTenantId),
    );

    const hasCompleteLlmConfiguration = useMemo(
        () => llmConfigurations && llmConfigurations.length > 0,
        [ llmConfigurations ],
    );

    const shouldShowModelValidationBanner = useMemo(() =>
        !isBannerDismissed &&
        !isLoadingLlmConfigs &&
        (isOnPrem && !isUnlicensedMode && isAdmin && !hasCompleteLlmConfiguration)
    , [
        isBannerDismissed,
        isLoadingLlmConfigs,
        isOnPrem,
        isUnlicensedMode,
        isAdmin,
        hasCompleteLlmConfiguration,
    ]);

    const EnableHomePageCarousel = useFeatureFlagValue(
        Features.EnableHomePageCarousel.name,
    );

    const isDedicated = useIsDedicated();

    const isProTrial = userProfile.accountUserDto.accountType === 'PRO-TRIAL';

    const userName = useMemo(() => firstName + ' ' + lastName, [ firstName, lastName ]);

    const greeting = useMemo((): string => {
        const currentHour = new Date().getHours();

        if (currentHour >= 3 && currentHour < 12) {
            return translate(
                { id: 'CLIENT_GOOD_MORNING_USER' },
                { 0: userName || translate({ id: 'CLIENT_USER' }) },
            );
        } else if (currentHour >= 12 && currentHour < 18) {
            return translate(
                { id: 'CLIENT_GOOD_AFTERNOON_USER' },
                { 0: userName || translate({ id: 'CLIENT_USER' }) },
            );
        }
        return translate(
            { id: 'CLIENT_GOOD_EVENING_USER' },
            { 0: userName || translate({ id: 'CLIENT_USER' }) },
        );
    }, [ translate, userName ]);

    const {
        isSSOEnabled: isSSOConnectionEnabled, isAADEnabled, isSAMLEnabled,
    } = useCheckSSOEnabled();

    const alertBannerMessage = useMemo(() => {
        const learnMoreLink = <ApLink
            target="_blank"
            rel="noreferrer"
            href={hyperLink.USER_MANAGEMENT}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                marginLeft: '8px',
            }}
        >
            <UiStack
                direction="row"
                align="center"
            >
                <UiText
                    style={{ display: 'inline' }}
                    data-cy="sso-banner-learn-more-link">
                    {translate({ id: 'CLIENT_AZURE_AD_CHECK_LINK_EMAILS_TOOLTIP_LEARN_MORE' })}
                </UiText>
                <LaunchIcon
                    style={{
                        fontSize: '14px',
                        marginLeft: '4px',
                    }}
                />
            </UiStack>
        </ApLink>;

        if (isAADEnabled) {
            return <>
                <UiText
                    style={{ display: 'inline' }}
                    data-cy='sso-banner-aad'>
                    {translate(
                        { id: 'CLIENT_SSO_SIGNED_IN_BANNER_AAD' },
                        {
                            link: (
                                <ApLink
                                    href={`${window.location.origin}/${organizationName}`}
                                >
                                    <UiText style={{ display: 'inline' }}>
                                        {`${window.location.origin}/${organizationName}`}
                                    </UiText>
                                </ApLink>
                            ),
                        },
                    )}
                </UiText>
                {learnMoreLink}
            </>;
        }
        if (isSAMLEnabled) {
            return <>
                <UiText
                    style={{ display: 'inline' }}
                    data-cy='sso-banner-saml'>
                    {translate(
                        { id: 'CLIENT_SSO_SIGNED_IN_BANNER_SAML' },
                        {
                            link: (
                                <ApLink
                                    href={`${window.location.origin}/${organizationName}`}
                                >
                                    <UiText style={{ display: 'inline' }}>
                                        {`${window.location.origin}/${organizationName}`}
                                    </UiText>
                                </ApLink>
                            ),
                        },
                    )}

                </UiText>
                {learnMoreLink}
            </>;
        }
        return <>
            <UiText
                style={{ display: 'inline' }}
                data-cy='sso-banner-default'>
                {translate(
                    { id: 'CLIENT_SSO_SIGNED_IN_BANNER_FALLBACK' },
                    {
                        link: (
                            <ApLink
                                href={`${window.location.origin}/${organizationName}`}
                            >
                                <UiText style={{ display: 'inline' }}>
                                    {`${window.location.origin}/${organizationName}`}
                                </UiText>
                            </ApLink>
                        ),
                    },
                )}
            </UiText>
            {learnMoreLink}
        </>;
    }, [ isAADEnabled, isSAMLEnabled, organizationName, translate ]);

    return (
        <UiPanel
            header={{
                title: greeting,
                fontSize: '18px',
                fontWeight: 700,
                isGreeting: true,
            }}
            sideBar={<HomePageSidebarLinksComponent />}
            licenseInGracePeriodBanner={<UiLicenseInGracePeriodBanner />}
            licenseExpiredBanner={<UiLicenseExpiredBanner />}
            licenseReactivationNeededBanner={<UiLicenseReactivationNeededBanner />}
            trialPerSkuLicenseInGracePeriodBanners={
                <UiTrialPerSkuLicenseInGracePeriodBanners />
            }
            banner={
                <>
                    {EnableHomePageCarousel && !isProTrial && <Carousel />}
                    {isSSOConnectionEnabled && isAuth0 && (
                        <UiAlertBanner
                            type="info"
                            closeable={false}
                            dataCy="sso-banner"
                        >
                            {alertBannerMessage}
                        </UiAlertBanner>
                    )}
                    <UiUpcomingMigrationBanner />
                </>
            }
        >
            {shouldShowModelValidationBanner && (
                <HomePageModelValidationComponent
                    onDismiss={() => setIsBannerDismissed(true)}
                />
            )}
            {isOnPrem && !isDedicated && isUnlicensedMode && <HomePageLicensesComponent />}
            <UiHomePage
                profile={userProfile}
                token={token} />
        </UiPanel>
    );
};

export default HomePageComponent;
